/**
 * Teachable custom gestures — landmark templates on top of MediaPipe hands.
 * Built-in classes stay canned; users hold a pose and we match a normalized hand shape.
 */

const KEY = "visionforge.customGestures.v1";

export type Point2 = { x: number; y: number };

export type CustomGesture = {
  id: string;
  name: string;
  /** Wrist-centered, unit-scaled 21×2 hand. */
  template: number[][];
  /** Mean landmark distance; lower is stricter. */
  threshold: number;
  createdAt: number;
};

const DEFAULT_THRESHOLD = 0.18;

let cache: CustomGesture[] | null = null;

export function newCustomGestureId(): string {
  return `cg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function slugGestureName(raw: string): string {
  const s = raw
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_]/g, "")
    .slice(0, 40);
  return s || "Custom";
}

export function loadCustomGestures(): CustomGesture[] {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      cache = [];
      return cache;
    }
    const parsed = JSON.parse(raw) as CustomGesture[];
    cache = Array.isArray(parsed)
      ? parsed.filter((g) => g?.id && g.name && Array.isArray(g.template) && g.template.length >= 21)
      : [];
    return cache;
  } catch {
    cache = [];
    return cache;
  }
}

export function saveCustomGestures(list: CustomGesture[]): CustomGesture[] {
  cache = list;
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
  return cache;
}

export function upsertCustomGesture(g: CustomGesture): CustomGesture[] {
  const list = loadCustomGestures();
  const i = list.findIndex((x) => x.id === g.id || x.name === g.name);
  const next = [...list];
  if (i >= 0) next[i] = g;
  else next.unshift(g);
  return saveCustomGestures(next);
}

export function deleteCustomGesture(id: string): CustomGesture[] {
  return saveCustomGestures(loadCustomGestures().filter((g) => g.id !== id));
}

export function normalizeHand(pts: Point2[]): number[][] | null {
  if (!pts || pts.length < 21) return null;
  const wrist = pts[0];
  const centered = pts.slice(0, 21).map((p) => ({
    x: p.x - wrist.x,
    y: p.y - wrist.y,
  }));
  let scale = 0;
  for (const p of centered) {
    const d = Math.hypot(p.x, p.y);
    if (d > scale) scale = d;
  }
  if (scale < 1e-5) return null;
  return centered.map((p) => [p.x / scale, p.y / scale]);
}

export function meanDistance(a: number[][], b: number[][]): number {
  const n = Math.min(a.length, b.length);
  if (!n) return 1;
  let s = 0;
  for (let i = 0; i < n; i++) {
    s += Math.hypot(a[i][0] - b[i][0], a[i][1] - b[i][1]);
  }
  return s / n;
}

export function averageTemplates(samples: number[][][]): number[][] | null {
  if (!samples.length) return null;
  const n = 21;
  const acc = Array.from({ length: n }, () => [0, 0]);
  for (const s of samples) {
    for (let i = 0; i < n; i++) {
      acc[i][0] += s[i]?.[0] ?? 0;
      acc[i][1] += s[i]?.[1] ?? 0;
    }
  }
  const k = samples.length;
  return acc.map((p) => [p[0] / k, p[1] / k]);
}

export function similarityFromDistance(d: number): number {
  return Math.max(0, Math.min(1, 1 - d / 0.42));
}

export function matchCustomGesture(
  pts: Point2[],
  list?: CustomGesture[],
): { name: string; confidence: number; id: string } | null {
  const norm = normalizeHand(pts);
  if (!norm) return null;
  const gestures = list ?? loadCustomGestures();
  let best: { g: CustomGesture; d: number } | null = null;
  for (const g of gestures) {
    const d = meanDistance(norm, g.template);
    const maxD = g.threshold || DEFAULT_THRESHOLD;
    if (d > maxD) continue;
    if (!best || d < best.d) best = { g, d };
  }
  if (!best) return null;
  return {
    name: best.g.name,
    confidence: similarityFromDistance(best.d),
    id: best.g.id,
  };
}

export const DEFAULT_CUSTOM_THRESHOLD = DEFAULT_THRESHOLD;
